import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Eye, EyeOff, Save, X, Image as ImageIcon, Move, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

// Admin → Herramientas → Banner Vendedores.
// Lets the admin upload a full-width horizontal image, add title/subtitle text
// on top of it, drag-to-reposition (like Facebook covers), toggle visibility, and
// see the uploaded file's format/dimensions/size.
export default function VendorsBannerAdmin() {
    const [form, setForm] = useState({
        vendors_banner_enabled: false,
        vendors_banner_image: "",
        vendors_banner_title: "",
        vendors_banner_subtitle: "",
        vendors_banner_cta_label: "",
        vendors_banner_cta_url: "",
        vendors_banner_image_position: "50% 50%",
    });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    // Metadata of the currently loaded image (width, height, weight, mime).
    const [imageMeta, setImageMeta] = useState(null);
    // Drag state — while true, mousemove/touchmove events reposition the image.
    const dragging = useRef(false);
    const dragStart = useRef({ x: 0, y: 0, posX: 50, posY: 50 });
    const fileRef = useRef(null);
    const previewRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/settings");
            const next = {
                vendors_banner_enabled: !!data.vendors_banner_enabled,
                vendors_banner_image: data.vendors_banner_image || "",
                vendors_banner_title: data.vendors_banner_title || "",
                vendors_banner_subtitle: data.vendors_banner_subtitle || "",
                vendors_banner_cta_label: data.vendors_banner_cta_label || "",
                vendors_banner_cta_url: data.vendors_banner_cta_url || "",
                vendors_banner_image_position: data.vendors_banner_image_position || "50% 50%",
            };
            setForm(next);
            if (next.vendors_banner_image) inspectImageFromUrl(next.vendors_banner_image);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error cargando ajustes");
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    // Reads intrinsic dimensions + approximate weight for any image (data URL or remote).
    const inspectImageFromUrl = (url) => {
        const img = new Image();
        img.onload = () => {
            let sizeKB = null;
            if (url.startsWith("data:")) {
                const b64 = url.split(",")[1] || "";
                sizeKB = Math.round((b64.length * 3) / 4 / 1024);
            }
            const mime = url.startsWith("data:")
                ? (url.substring(5, url.indexOf(";")) || "image").split("/").pop().toUpperCase()
                : "URL";
            setImageMeta({ width: img.naturalWidth, height: img.naturalHeight, sizeKB, mime });
        };
        img.onerror = () => setImageMeta(null);
        img.src = url;
    };

    const pickFile = () => fileRef.current?.click();

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) { toast.error("Solo imágenes"); return; }
        if (f.size > 5 * 1024 * 1024) { toast.error("La imagen supera 5MB"); return; }
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result;
            // Reset the crop to centered so the newly uploaded image is fully visible before dragging.
            setForm((prev) => ({ ...prev, vendors_banner_image: dataUrl, vendors_banner_image_position: "50% 50%" }));
            inspectImageFromUrl(dataUrl);
        };
        reader.readAsDataURL(f);
    };

    // ------- Drag to reposition (mouse + touch) -------
    const parsePos = (str) => {
        const parts = (str || "50% 50%").split(" ");
        return {
            x: parseFloat(parts[0]) || 50,
            y: parseFloat(parts[1]) || 50,
        };
    };
    const clamp = (v) => Math.max(0, Math.min(100, v));

    const onDragStart = (clientX, clientY) => {
        if (!form.vendors_banner_image) return;
        dragging.current = true;
        const { x, y } = parsePos(form.vendors_banner_image_position);
        dragStart.current = { x: clientX, y: clientY, posX: x, posY: y };
    };

    const onDragMove = (clientX, clientY) => {
        if (!dragging.current || !previewRef.current) return;
        const rect = previewRef.current.getBoundingClientRect();
        // Convert pixel delta to percentage (drag right → image shifts left → posX decreases,
        // which is what object-position expects: 0% = show left edge).
        const dx = ((clientX - dragStart.current.x) / rect.width) * 100;
        const dy = ((clientY - dragStart.current.y) / rect.height) * 100;
        const nextX = clamp(dragStart.current.posX - dx);
        const nextY = clamp(dragStart.current.posY - dy);
        setForm((prev) => ({ ...prev, vendors_banner_image_position: `${nextX.toFixed(1)}% ${nextY.toFixed(1)}%` }));
    };

    const onDragEnd = () => { dragging.current = false; };

    // Global mouseup listener — release the drag even if the pointer leaves the preview.
    useEffect(() => {
        const up = () => onDragEnd();
        const move = (e) => onDragMove(e.clientX, e.clientY);
        window.addEventListener("mouseup", up);
        window.addEventListener("mousemove", move);
        return () => {
            window.removeEventListener("mouseup", up);
            window.removeEventListener("mousemove", move);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.vendors_banner_image_position, form.vendors_banner_image]);

    const resetPosition = () => setForm((prev) => ({ ...prev, vendors_banner_image_position: "50% 50%" }));

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/settings", form);
            toast.success("Banner de Vendedores guardado");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error guardando");
        } finally { setSaving(false); }
    };

    const toggle = async () => {
        try {
            const next = !form.vendors_banner_enabled;
            await api.put("/settings", { ...form, vendors_banner_enabled: next });
            setForm((prev) => ({ ...prev, vendors_banner_enabled: next }));
            toast.success(next ? "Banner activado" : "Banner desactivado");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };

    if (loading) return <div className="text-sm text-[var(--tb-muted)]">Cargando…</div>;

    const hasImage = !!form.vendors_banner_image;
    const objectPosition = form.vendors_banner_image_position || "50% 50%";

    return (
        <div className="space-y-4" data-testid="vendors-banner-admin">
            <div className="tb-card p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-display text-lg mb-1">Banner en Vendedores</h3>
                        <p className="text-xs text-[var(--tb-text-2)] leading-relaxed max-w-2xl">
                            Sube una imagen horizontal que aparecerá <strong>hasta arriba</strong> de la página
                            <code> /vendedores</code>. Arrastra la vista previa para reposicionar la parte de la imagen
                            que se ve, escribe un título/subtítulo y activa el banner cuando quieras publicarlo.
                        </p>
                    </div>
                    <button onClick={toggle}
                        className={`tb-btn-secondary !text-xs inline-flex items-center gap-1.5 ${form.vendors_banner_enabled ? "!text-emerald-300 !border-emerald-400/50" : "!text-rose-300 !border-rose-400/50"}`}
                        data-testid="vendors-banner-toggle">
                        {form.vendors_banner_enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        {form.vendors_banner_enabled ? "Visible" : "Oculto"}
                    </button>
                </div>
            </div>

            {/* Preview + drag */}
            <div className="tb-card p-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="tb-overline text-[var(--tb-gold)]">Vista previa</div>
                    {hasImage && (
                        <div className="flex items-center gap-1 text-[10px] text-[var(--tb-muted)]">
                            <Move className="w-3 h-3" /> Arrastra para reacomodar
                            <button onClick={resetPosition} className="ml-2 tb-btn-ghost !p-1 !text-[10px] inline-flex items-center gap-0.5" title="Restaurar centro" data-testid="vendors-banner-reset-pos">
                                <RotateCcw className="w-3 h-3" /> Centrar
                            </button>
                        </div>
                    )}
                </div>
                {hasImage ? (
                    <div
                        ref={previewRef}
                        onMouseDown={(e) => { e.preventDefault(); onDragStart(e.clientX, e.clientY); }}
                        onTouchStart={(e) => onDragStart(e.touches[0].clientX, e.touches[0].clientY)}
                        onTouchMove={(e) => onDragMove(e.touches[0].clientX, e.touches[0].clientY)}
                        onTouchEnd={onDragEnd}
                        className={`relative rounded-lg overflow-hidden border border-[var(--tb-border)] select-none ${dragging.current ? "cursor-grabbing" : "cursor-grab"}`}
                        data-testid="vendors-banner-preview">
                        <img src={form.vendors_banner_image} alt="Preview"
                            style={{ objectPosition }}
                            draggable={false}
                            className="w-full h-40 md:h-56 object-cover pointer-events-none" />
                        {(form.vendors_banner_title || form.vendors_banner_subtitle) && (
                            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent flex flex-col justify-center px-6 md:px-10 pointer-events-none">
                                {form.vendors_banner_title && <div className="font-display text-xl md:text-3xl text-white drop-shadow-lg">{form.vendors_banner_title}</div>}
                                {form.vendors_banner_subtitle && <div className="text-xs md:text-sm text-white/85 mt-1 max-w-md drop-shadow">{form.vendors_banner_subtitle}</div>}
                                {form.vendors_banner_cta_label && (
                                    <span className="inline-block mt-2 self-start px-3 py-1.5 bg-[var(--tb-gold)] text-black text-xs font-semibold rounded">{form.vendors_banner_cta_label}</span>
                                )}
                            </div>
                        )}
                        {/* Position indicator overlay (only while dragging) */}
                        {dragging.current && (
                            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-mono px-2 py-1 rounded">
                                {objectPosition}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-32 border border-dashed border-[var(--tb-border)] rounded text-xs text-[var(--tb-muted)]">
                        <ImageIcon className="w-4 h-4 mr-1" /> Sube una imagen para ver la vista previa
                    </div>
                )}

                {/* Metadata */}
                {imageMeta && (
                    <div className="mt-3 flex flex-wrap gap-4 text-[10px] text-[var(--tb-muted)] uppercase tracking-wider" data-testid="vendors-banner-meta">
                        <span><strong className="text-[var(--tb-gold)]">Formato:</strong> {imageMeta.mime}</span>
                        <span><strong className="text-[var(--tb-gold)]">Dimensiones:</strong> {imageMeta.width} × {imageMeta.height} px</span>
                        {imageMeta.sizeKB != null && (
                            <span><strong className="text-[var(--tb-gold)]">Peso:</strong> {imageMeta.sizeKB > 1024 ? `${(imageMeta.sizeKB / 1024).toFixed(2)} MB` : `${imageMeta.sizeKB} KB`}</span>
                        )}
                        <span><strong className="text-[var(--tb-gold)]">Posición:</strong> {objectPosition}</span>
                        {imageMeta.width && imageMeta.height && (
                            <span><strong className="text-[var(--tb-gold)]">Ratio:</strong> {(imageMeta.width / imageMeta.height).toFixed(2)}:1</span>
                        )}
                    </div>
                )}
            </div>

            {/* Form */}
            <div className="tb-card p-4 space-y-3">
                <div>
                    <label className="tb-overline text-[var(--tb-gold)]">1. Imagen del banner</label>
                    <div className="flex items-center gap-2 mt-1">
                        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
                        <button onClick={pickFile} className="tb-btn-secondary !text-xs inline-flex items-center gap-1" data-testid="vendors-banner-upload">
                            <Upload className="w-3.5 h-3.5" /> {hasImage ? "Cambiar imagen" : "Subir imagen"}
                        </button>
                        {hasImage && (
                            <button onClick={() => { setForm({ ...form, vendors_banner_image: "", vendors_banner_image_position: "50% 50%" }); setImageMeta(null); }}
                                className="tb-btn-ghost !p-1.5 text-rose-400 hover:text-rose-300" title="Quitar imagen">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    <p className="text-[10px] text-[var(--tb-muted)] mt-1">
                        Formato recomendado: 1600×400 (relación 4:1). Máximo 5MB. JPG o PNG.
                    </p>
                </div>
                <div>
                    <label className="tb-overline text-[var(--tb-gold)]">2. Título (encima del banner)</label>
                    <input className="tb-input mt-1" value={form.vendors_banner_title}
                        onChange={(e) => setForm({ ...form, vendors_banner_title: e.target.value })}
                        placeholder="Ej. Presentamos: Aroma Design Studio" maxLength={80}
                        data-testid="vendors-banner-title" />
                </div>
                <div>
                    <label className="tb-overline text-[var(--tb-gold)]">3. Subtítulo (texto informativo corto)</label>
                    <textarea className="tb-input mt-1 min-h-[60px]" value={form.vendors_banner_subtitle}
                        onChange={(e) => setForm({ ...form, vendors_banner_subtitle: e.target.value })}
                        placeholder="Ej. Fragancias artesanales inspiradas en Centroamérica. Descubre la colección."
                        maxLength={200} data-testid="vendors-banner-subtitle" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="tb-overline text-[var(--tb-gold)]">4. Botón (texto — opcional)</label>
                        <input className="tb-input mt-1" value={form.vendors_banner_cta_label}
                            onChange={(e) => setForm({ ...form, vendors_banner_cta_label: e.target.value })}
                            placeholder="Ej. Visitar el sitio" maxLength={30}
                            data-testid="vendors-banner-cta-label" />
                    </div>
                    <div>
                        <label className="tb-overline text-[var(--tb-gold)]">Botón (URL — opcional)</label>
                        <input className="tb-input mt-1" value={form.vendors_banner_cta_url}
                            onChange={(e) => setForm({ ...form, vendors_banner_cta_url: e.target.value })}
                            placeholder="https://…" data-testid="vendors-banner-cta-url" />
                    </div>
                </div>
                <div className="flex justify-end pt-2 border-t border-[var(--tb-border)]">
                    <button onClick={save} disabled={saving} className="tb-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="vendors-banner-save">
                        <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar cambios"}
                    </button>
                </div>
            </div>
        </div>
    );
}
